import React, { useState, useEffect } from 'react';
import styled, { ThemeProvider, createGlobalStyle } from 'styled-components';
import {
  FaWhatsapp, FaInstagram, FaEnvelope, FaBars, FaTimes, FaLeaf,
  FaBrain, FaDna, FaUsers, FaArrowRight, FaHeart, FaSpa,
  FaCoins, FaUserShield, FaGraduationCap,
  FaCalendarCheck, FaRoute, FaFileMedicalAlt, FaBalanceScale, FaPuzzlePiece, FaBriefcase
} from 'react-icons/fa';

// --- IMPORTANTE: CERTIFIQUE-SE QUE O ARQUIVO EXISTE NA PASTA SRC ---
import foto from './image_774a99.jpg';

// --- CONFIGURAÇÃO WHATSAPP ---
const WHATSAPP_NUMBER = "5511943931638";
const getWaUrl = (text) => `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;

// --- THEME ---
export const theme = {
  colors: {
    sageLight: '#E8EFEC',
    sageMedium: '#C4D6CC',
    sageDark: '#8FA394',
    earthDark: '#4A4A4A',
    sand: '#F9F7F2',
    goldMuted: '#D4C5A9',
    white: '#FFFFFF',
  },
  fonts: {
    sans: "'Montserrat', sans-serif",
    serif: "'Cormorant Garamond', serif",
  },
  breakpoints: {
    mobile: '480px',
    tablet: '768px',
    laptop: '1024px',
  }
};

// --- GLOBAL STYLES ---
const GlobalStyles = createGlobalStyle`
  * { margin: 0; padding: 0; box-sizing: border-box; }
  html { scroll-behavior: smooth; font-size: 16px; }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    html { font-size: 14px; }
  }

  body {
    background-color: ${theme.colors.sand};
    font-family: ${theme.fonts.sans};
    color: ${theme.colors.earthDark};
    overflow-x: hidden;
    -webkit-font-smoothing: antialiased;
  }
  
  @keyframes float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-10px); }
  }
  @keyframes spin-slow {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  @keyframes pulse-green {
    0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(37, 211, 102, 0.7); }
    70% { transform: scale(1.1); box-shadow: 0 0 0 10px rgba(37, 211, 102, 0); }
    100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(37, 211, 102, 0); }
  }

  .reveal {
    opacity: 0;
    transform: translateY(30px);
    transition: all 1s ease-out;
  }
  .reveal.active {
    opacity: 1;
    transform: translateY(0);
  }
`;

// --- STYLED COMPONENTS ---

const Container = styled.div`
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: 0 1.5rem;
  position: relative;
  z-index: 10;

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 0 1rem;
  }
`;

const GlassCard = styled.div`
  background: rgba(255, 255, 255, 0.65);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 2rem;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  text-decoration: none;
  display: block;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-8px);
    background: rgba(255, 255, 255, 0.9);
    box-shadow: 0 20px 40px -10px rgba(143, 163, 148, 0.3);
    border-color: ${theme.colors.sageMedium};
  }

  @media (max-width: ${theme.breakpoints.tablet}) {
    &:hover { transform: translateY(-2px); }
  }
`;

const Nav = styled.nav`
  position: fixed;
  width: 100%;
  z-index: 50;
  padding-top: ${props => props.$scrolled ? '1rem' : '2rem'};
  transition: all 0.5s ease;

  @media (max-width: ${theme.breakpoints.tablet}) {
    padding-top: 1rem;
  }
`;

const NavGlass = styled.div`
  background: ${props => props.$scrolled ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.7)'};
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.5);
  box-shadow: ${props => props.$scrolled ? '0 4px 30px rgba(0,0,0,0.05)' : 'none'};
  border-radius: 999px;
  padding: 0.75rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.5s;

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 0.75rem 1.5rem;
  }
`;

const Logo = styled.a`
  font-family: ${theme.fonts.serif};
  font-size: 1.5rem;
  font-weight: 700;
  color: ${theme.colors.earthDark};
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  span { color: ${theme.colors.sageDark}; font-size: 1.8rem; }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.2rem;
    span { font-size: 1.5rem; }
  }
`;

const NavLinks = styled.div`
  display: none;
  @media(min-width: ${theme.breakpoints.tablet}) {
    display: flex;
    gap: 1.5rem;
    align-items: center;
  }
  @media(min-width: ${theme.breakpoints.laptop}) {
    gap: 2rem;
  }

  a {
    text-decoration: none;
    color: ${theme.colors.earthDark};
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.15em;
    font-weight: 500;
    transition: color 0.3s;
    &:hover { color: ${theme.colors.sageDark}; }
  }
`;

const MobileMenuToggle = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: ${theme.colors.earthDark};
  cursor: pointer;
  display: none;
  
  @media(max-width: ${theme.breakpoints.tablet}) {
    display: block;
  }
`;

const MobileMenuContainer = styled(GlassCard)`
  position: absolute; 
  top: 90px; 
  left: 1rem; 
  right: 1rem; 
  padding: 2rem; 
  display: flex; 
  flex-direction: column; 
  gap: 1.5rem; 
  z-index: 40; 
  text-align: center;
  animation: float 0.5s ease-out;
`;

const Button = styled.a`
  background-color: ${props => props.$outline ? 'transparent' : theme.colors.sageDark};
  color: ${props => props.$outline ? theme.colors.earthDark : theme.colors.white};
  border: ${props => props.$outline ? `1px solid ${theme.colors.sageDark}` : 'none'};
  padding: 0.8rem 1.8rem;
  border-radius: 999px;
  text-decoration: none;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: ${props => props.$outline ? 'none' : '0 10px 20px -5px rgba(143, 163, 148, 0.5)'};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  white-space: nowrap;

  &:hover {
    background-color: ${props => props.$outline ? theme.colors.sageLight : '#7d9182'};
    transform: translateY(-2px);
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 100%;
    padding: 1rem;
  }
`;

const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  padding-top: 8rem;
  padding-bottom: 4rem;
  position: relative;
  overflow: hidden;

  @media (max-width: ${theme.breakpoints.tablet}) {
    padding-top: 7rem;
    min-height: auto;
  }
`;

const HeroGrid = styled.div`
  display: flex;
  flex-direction: column-reverse;
  gap: 3rem;
  align-items: center;
  
  @media(min-width: ${theme.breakpoints.laptop}) {
    flex-direction: row;
    gap: 4rem;
    justify-content: space-between;
  }
`;

const HeroTextContent = styled.div`
  flex: 1;
  max-width: 600px;
  text-align: center;
  
  @media(min-width: ${theme.breakpoints.laptop}) {
    text-align: left;
  }
`;

const Title = styled.h1`
  font-family: ${theme.fonts.serif};
  color: ${theme.colors.earthDark};
  font-size: 2.5rem;
  line-height: 1.1;
  font-weight: 400;
  margin-bottom: 1.5rem;
  
  @media(min-width: ${theme.breakpoints.tablet}) { font-size: 3.5rem; }
  @media(min-width: ${theme.breakpoints.laptop}) { font-size: 4rem; }

  span.subtitle {
    display: block;
    font-family: ${theme.fonts.sans};
    font-size: 0.9rem;
    color: ${theme.colors.sageDark};
    letter-spacing: 0.2em;
    margin-bottom: 1rem;
    text-transform: uppercase;
    font-weight: 600;
  }
  
  span.highlight {
    font-style: italic;
    color: ${theme.colors.sageDark};
  }
`;

const Description = styled.p`
  color: #5C5C5C;
  font-size: 1rem;
  line-height: 1.8;
  margin-bottom: 2.5rem;
`;

const HeroButtons = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  justify-content: center;
  
  @media(min-width: ${theme.breakpoints.laptop}) {
    justify-content: flex-start;
  }
`;

const HeroImageContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  position: relative;
  width: 100%;
`;

const HeroImageWrapper = styled.div`
  position: relative;
  background-color: #F9F7F2;
  width: 100%;
  max-width: 400px;
  height: auto;
  margin: 0 auto;
  z-index: 1;
`;

const DecorativeCircle = styled.div`
  position: absolute;
  top: -20px; left: -20px; right: -20px; bottom: -20px;
  background-color: rgba(0, 0, 0, 0);
  border: 1px dashed ${theme.colors.sageDark};
  border-radius: 42% 58% 70% 30% / 45% 45% 55% 55%;
  animation: spin-slow 20s linear infinite;
  z-index: -1;
  opacity: 0.6;
`;

const HeroImg = styled.img`
  width: 100%;
  height: auto;
  display: block;
  border-radius: 100px 100px 20px 20px;
  object-fit: cover;
`;

const WhatsAppFloat = styled.a`
  position: fixed; bottom: 2rem; right: 2rem;
  width: 4rem; height: 4rem;
  background-color: #25d366; 
  color: white;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 2rem; z-index: 100;
  box-shadow: 0 10px 25px -5px rgba(37, 211, 102, 0.5);
  animation: pulse-green 2.5s infinite;
  transition: all 0.3s;
  &:hover { background-color: #20ba5a; transform: scale(1.1); }
`;

const Blob = styled.div`
  position: absolute; border-radius: 50%; filter: blur(80px); z-index: -1;
  opacity: ${props => props.opacity || 0.4};
  background-color: ${props => props.color};
  width: ${props => props.size}; height: ${props => props.size};
  top: ${props => props.$top}; 
  left: ${props => props.$left};
  right: ${props => props.$right}; 
  bottom: ${props => props.$bottom};
  
  @media(max-width: ${theme.breakpoints.tablet}) {
    width: 50%; height: auto; aspect-ratio: 1/1;
  }
`;

const SobreGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 4rem;
  align-items: center;
  @media(min-width: ${theme.breakpoints.laptop}) {
    grid-template-columns: 1fr 1fr;
  }
`;

const AboutDetails = styled.div`
  margin-top: 3rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const AboutItem = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
  padding: 1rem;
  background: rgba(255,255,255,0.4);
  border-radius: 1rem;
  transition: all 0.3s;
  &:hover { background: rgba(255,255,255,0.7); transform: translateX(5px); }
  
  @media(max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
    gap: 1rem;
    align-items: center;
    text-align: center;
  }

  .icon-box {
    background: white;
    padding: 1rem;
    border-radius: 50%;
    color: ${theme.colors.sageDark};
    box-shadow: 0 5px 15px rgba(0,0,0,0.05);
    flex-shrink: 0;
  }
`;

const CRPContainer = styled.div`
  display: flex; 
  gap: 1.5rem; 
  margin-top: 3rem;
  flex-wrap: wrap;
  @media(max-width: ${theme.breakpoints.mobile}) { justify-content: center; }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
`;

const StepsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 3rem;
  margin-top: 4rem;
  position: relative;
  @media(min-width: ${theme.breakpoints.tablet}) { grid-template-columns: repeat(3, 1fr); }

  &::before {
    content: '';
    position: absolute;
    top: 3rem; left: 10%; right: 10%;
    height: 2px;
    background: linear-gradient(90deg, transparent, ${theme.colors.sageMedium}, transparent);
    z-index: 0;
    display: none;
    @media(min-width: ${theme.breakpoints.laptop}) { display: block; }
  }
`;

const StepCard = styled.div`
  text-align: center;
  position: relative;
  z-index: 1;
  
  .step-icon {
    width: 6rem; height: 6rem;
    background: white;
    border-radius: 50%;
    margin: 0 auto 1.5rem;
    display: flex; align-items: center; justify-content: center;
    font-size: 2rem;
    color: ${theme.colors.sageDark};
    box-shadow: 0 10px 30px -10px rgba(0,0,0,0.1);
    border: 1px solid ${theme.colors.sageLight};
    transition: all 0.3s;
  }

  &:hover .step-icon {
    transform: scale(1.1);
    background: ${theme.colors.sageDark};
    color: white;
  }
`;

const FAQGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 1.5rem;
  @media(max-width: ${theme.breakpoints.mobile}) { grid-template-columns: 1fr; }
`;

const FAQCard = styled(GlassCard)`
  padding: 2rem;
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  @media(max-width: ${theme.breakpoints.mobile}) {
    padding: 1.5rem;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`;

// --- COMPONENTE APP ---

const useReveal = () => {
  useEffect(() => {
    const handleScroll = () => {
      const reveals = document.querySelectorAll(".reveal");
      for (let i = 0; i < reveals.length; i++) {
        if (reveals[i].getBoundingClientRect().top < window.innerHeight - 80) {
          reveals[i].classList.add("active");
        }
      }
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
};

function App() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  useReveal();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const closeMenu = () => setMenuOpen(false);

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles />

      <div style={{ position: 'fixed', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 0 }}>
        <Blob color={theme.colors.sageMedium} size="35rem" $top="-10%" $left="-10%" opacity={0.3} />
        <Blob color="#F0EBE0" size="40rem" $top="30%" $right="-15%" opacity={0.5} />
      </div>

      <Nav $scrolled={scrolled}>
        <Container>
          <NavGlass $scrolled={scrolled}>
            <Logo href="#"><span><FaSpa /></span> Luana Mayara</Logo>

            <NavLinks>
              <a href="#home">Início</a>
              <a href="#sobre">Sobre</a>
              <a href="#atuacao">Atuação</a>
              <a href="#faq">Dúvidas</a>
              <Button href={getWaUrl("Olá Luana, gostaria de agendar um horário.")} target="_blank">Agendar</Button>
            </NavLinks>

            <MobileMenuToggle onClick={() => setMenuOpen(!menuOpen)}>
              {menuOpen ? <FaTimes /> : <FaBars />}
            </MobileMenuToggle>
          </NavGlass>

          {menuOpen && (
            <MobileMenuContainer>
              <a href="#home" onClick={closeMenu} style={{ textDecoration: 'none', color: theme.colors.earthDark }}>INÍCIO</a>
              <a href="#sobre" onClick={closeMenu} style={{ textDecoration: 'none', color: theme.colors.earthDark }}>SOBRE</a>
              <a href="#atuacao" onClick={closeMenu} style={{ textDecoration: 'none', color: theme.colors.earthDark }}>ATUAÇÃO</a>
              <a href="#faq" onClick={closeMenu} style={{ textDecoration: 'none', color: theme.colors.earthDark }}>DÚVIDAS</a>
              <Button href={getWaUrl("Olá, gostaria de agendar pelo celular.")} target="_blank" onClick={closeMenu}>Agendar</Button>
            </MobileMenuContainer>
          )}
        </Container>
      </Nav>

      <HeroSection id="home">
        <Container>
          <HeroGrid>
            <HeroTextContent className="reveal active">
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', background: 'rgba(255,255,255,0.5)', borderRadius: '50px', marginBottom: '1.5rem', fontSize: '0.75rem', fontWeight: 'bold', color: theme.colors.sageDark, border: `1px solid ${theme.colors.white}` }}>
                <div style={{ width: 8, height: 8, background: theme.colors.sageDark, borderRadius: '50%' }}></div>
                PSICOLOGIA ONLINE & BEM-ESTAR
              </div>
              <Title>
                <span className="subtitle">Psicóloga Luana Mayara</span>
                Um espaço seguro para <span className="highlight">recomeçar.</span>
              </Title>
              <Description>
                Atendimento psicológico online com empatia e ciência. Especialista em <strong>TCC</strong> e <strong>Neuropsicologia</strong>, guiando você em sua jornada de autoconhecimento.
              </Description>
              <HeroButtons>
                <Button href={getWaUrl("Olá Luana, vim pelo site e quero iniciar a terapia.")} target="_blank" style={{ padding: '1rem 2rem' }}>
                  Iniciar Terapia <FaArrowRight style={{ fontSize: '0.8em' }} />
                </Button>
                <Button href="#sobre" $outline style={{ padding: '1rem 2rem' }}>Conhecer Luana</Button>
              </HeroButtons>
            </HeroTextContent>

            <HeroImageContainer className="reveal">
              <HeroImageWrapper>
                <DecorativeCircle />
                <HeroImg src={foto} alt="Psicóloga Luana Mayara" />
                <GlassCard style={{ position: 'absolute', bottom: '2rem', left: '-1rem', padding: '1rem 1.5rem', display: 'flex', alignItems: 'center', gap: '1rem', width: 'auto', animation: 'float 6s ease-in-out infinite' }}>
                  <div style={{ backgroundColor: theme.colors.sageLight, padding: '0.8rem', borderRadius: '50%', color: theme.colors.sageDark }}><FaLeaf size={20} /></div>
                  <div>
                    <p style={{ fontSize: '0.65rem', fontWeight: 'bold', textTransform: 'uppercase', color: '#9CA3AF' }}>Abordagem</p>
                    <p style={{ fontFamily: theme.fonts.serif, fontSize: '1.1rem', color: theme.colors.earthDark, fontStyle: 'italic' }}>Humanizada</p>
                  </div>
                </GlassCard>
              </HeroImageWrapper>
            </HeroImageContainer>
          </HeroGrid>
        </Container>
      </HeroSection>

      <section style={{ padding: '4rem 0', textAlign: 'center' }}>
        <Container className="reveal">
          <FaHeart size={24} color={theme.colors.goldMuted} style={{ marginBottom: '1.5rem' }} />
          <h2 style={{ fontFamily: theme.fonts.serif, fontSize: 'clamp(1.5rem, 4vw, 2rem)', fontStyle: 'italic', color: theme.colors.earthDark, maxWidth: '45rem', margin: '0 auto', lineHeight: '1.5' }}>
            "O autoconhecimento é o primeiro passo para a cura. Vamos caminhar juntos nessa jornada de transformação."
          </h2>
        </Container>
      </section>

      <section id="sobre" style={{ padding: '6rem 0', backgroundColor: 'rgba(255,255,255,0.3)' }}>
        <Container>
          <SobreGrid>
            <div className="reveal">
              <span style={{ color: theme.colors.sageDark, fontWeight: 'bold', letterSpacing: '0.3em', textTransform: 'uppercase', fontSize: '0.75rem', display: 'block', marginBottom: '1rem' }}>Quem sou eu</span>
              <h2 style={{ fontFamily: theme.fonts.serif, fontSize: 'clamp(2.5rem, 5vw, 3.5rem)', color: theme.colors.earthDark, lineHeight: '1', marginBottom: '2rem' }}>
                Luana <span style={{ fontStyle: 'italic', color: '#9ca3af' }}>Mayara</span>
              </h2>
              <div style={{ color: '#4b5563', fontSize: '1.125rem', lineHeight: '1.8', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <p>Minha trajetória na psicologia é guiada pela crença profunda de que todo ser humano possui a capacidade inata de se reinventar.</p>
                <p>Com sólida experiência clínica e vivência corporativa, entendo as nuances da pressão moderna. Especializo-me em <strong>Neuropsicologia</strong> e <strong>TCC</strong> para oferecer um tratamento baseado em evidências, mas entregue com alma.</p>
              </div>

              <CRPContainer>
                <GlassCard style={{ padding: '1.5rem', textAlign: 'center', minWidth: '150px' }}>
                  <span style={{ fontFamily: theme.fonts.serif, fontSize: '1.8rem', color: theme.colors.sageDark, display: 'block' }}>CRP</span>
                  <span style={{ fontSize: '0.65rem', letterSpacing: '0.2em', color: '#6b7280' }}>06/202392</span>
                </GlassCard>
                <GlassCard style={{ padding: '1.5rem', textAlign: 'center', minWidth: '150px' }}>
                  <span style={{ fontSize: '1.8rem', color: theme.colors.sageDark, display: 'block', marginBottom: '0.2rem' }}><FaUsers /></span>
                  <span style={{ fontSize: '0.65rem', letterSpacing: '0.2em', color: '#6b7280' }}>ONLINE & SEGURO</span>
                </GlassCard>
              </CRPContainer>
            </div>

            <div className="reveal">
              <AboutDetails>
                <AboutItem>
                  <div className="icon-box"><FaGraduationCap size={20} /></div>
                  <div>
                    <h3 style={{ fontFamily: theme.fonts.serif, fontSize: '1.4rem', marginBottom: '0.5rem' }}>Formação Contínua</h3>
                    <p style={{ fontSize: '0.9rem', color: '#666', lineHeight: '1.5' }}>Pós-graduanda em Neuropsicologia e TCC. Bacharel em Psicologia pelo CEUNSP.</p>
                  </div>
                </AboutItem>

                <AboutItem>
                  <div className="icon-box"><FaHeart size={20} /></div>
                  <div>
                    <h3 style={{ fontFamily: theme.fonts.serif, fontSize: '1.4rem', marginBottom: '0.5rem' }}>Propósito Social</h3>
                    <p style={{ fontSize: '0.9rem', color: '#666', lineHeight: '1.5' }}>Atuação voluntária no "Cria Brasil" e "Projeto Que Seja", levando acolhimento a quem precisa.</p>
                  </div>
                </AboutItem>

                <AboutItem>
                  <div className="icon-box"><FaBriefcase size={20} /></div>
                  <div>
                    <h3 style={{ fontFamily: theme.fonts.serif, fontSize: '1.4rem', marginBottom: '0.5rem' }}>Visão Corporativa</h3>
                    <p style={{ fontSize: '0.9rem', color: '#666', lineHeight: '1.5' }}>Background sólido em RH e administrativo, compreendendo as dores do mundo do trabalho.</p>
                  </div>
                </AboutItem>
              </AboutDetails>
            </div>
          </SobreGrid>
        </Container>
      </section>

      <section id="atuacao" style={{ padding: '6rem 0', backgroundColor: 'rgba(255,255,255,0.4)' }}>
        <Container>
          <div style={{ textAlign: 'center', marginBottom: '4rem' }} className="reveal">
            <span style={{ fontSize: '0.75rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: theme.colors.sageDark, fontWeight: 'bold' }}>Terapias & Abordagens</span>
            <h2 style={{ fontFamily: theme.fonts.serif, fontSize: 'clamp(2rem, 4vw, 3rem)', marginTop: '0.5rem' }}>Como posso te ajudar?</h2>
          </div>

          <ServicesGrid>
            <GlassCard as="a" href={getWaUrl("Tenho interesse em TCC.")} target="_blank" className="reveal" style={{ padding: '2.5rem', cursor: 'pointer' }}>
              <div style={{ backgroundColor: 'white', width: '3.5rem', height: '3.5rem', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', color: theme.colors.sageDark, marginBottom: '1.5rem' }}><FaBrain /></div>
              <h3 style={{ fontFamily: theme.fonts.serif, fontSize: '1.8rem', marginBottom: '1rem', color: theme.colors.earthDark }}>TCC</h3>
              <p style={{ color: '#666', lineHeight: '1.6', marginBottom: '2rem', fontSize: '0.95rem' }}>Terapia Cognitivo Comportamental. Uma abordagem estruturada e eficaz para ressignificar padrões.</p>
              <span style={{ color: theme.colors.sageDark, fontWeight: 'bold', fontSize: '0.75rem', letterSpacing: '0.1em', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>Saiba mais <FaArrowRight /></span>
            </GlassCard>

            <GlassCard as="a" href={getWaUrl("Tenho interesse na análise  Neuropsicológica.")} target="_blank" className="reveal" style={{ padding: '2.5rem', cursor: 'pointer' }}>
              <div style={{ backgroundColor: theme.colors.sageDark, width: '3.5rem', height: '3.5rem', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', color: 'white', marginBottom: '1.5rem' }}><FaDna /></div>
              <h3 style={{ fontFamily: theme.fonts.serif, fontSize: '1.8rem', marginBottom: '1rem', color: theme.colors.earthDark }}>Neuropsicologia</h3>
              <p style={{ color: '#666', lineHeight: '1.6', marginBottom: '2rem', fontSize: '0.95rem' }}>Análise detalhada das funções cognitivas, memória e atenção.</p>
              <span style={{ color: theme.colors.sageDark, fontWeight: 'bold', fontSize: '0.75rem', letterSpacing: '0.1em', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>Saiba mais <FaArrowRight /></span>
            </GlassCard>

            <GlassCard as="a" href={getWaUrl("Tenho interesse nos serviços Corporativos.")} target="_blank" className="reveal" style={{ padding: '2.5rem', cursor: 'pointer' }}>
              <div style={{ backgroundColor: 'white', width: '3.5rem', height: '3.5rem', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', color: theme.colors.sageDark, marginBottom: '1.5rem' }}><FaUsers /></div>
              <h3 style={{ fontFamily: theme.fonts.serif, fontSize: '1.8rem', marginBottom: '1rem', color: theme.colors.earthDark }}>Corporativo</h3>
              <p style={{ color: '#666', lineHeight: '1.6', marginBottom: '2rem', fontSize: '0.95rem' }}>Consultoria para empresas e equipes. Fortalecendo a cultura de bem-estar.</p>
              <span style={{ color: theme.colors.sageDark, fontWeight: 'bold', fontSize: '0.75rem', letterSpacing: '0.1em', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>Saiba mais <FaArrowRight /></span>
            </GlassCard>
          </ServicesGrid>
        </Container>
      </section>

      <section style={{ padding: '6rem 0' }}>
        <Container>
          <div style={{ textAlign: 'center' }} className="reveal">
            <span style={{ fontSize: '0.75rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: theme.colors.sageDark, fontWeight: 'bold' }}>Passo a Passo</span>
            <h2 style={{ fontFamily: theme.fonts.serif, fontSize: 'clamp(2rem, 4vw, 3rem)', marginTop: '0.5rem', color: theme.colors.earthDark }}>Como iniciar seu atendimento</h2>
          </div>

          <StepsGrid className="reveal">
            <StepCard>
              <div className="step-icon"><FaWhatsapp /></div>
              <h3 style={{ fontFamily: theme.fonts.serif, fontSize: '1.5rem', marginBottom: '0.5rem' }}>1. Contato</h3>
              <p style={{ color: '#666', fontSize: '0.9rem' }}>Envie uma mensagem para agendarmos sua primeira conversa.</p>
            </StepCard>

            <StepCard>
              <div className="step-icon"><FaCalendarCheck /></div>
              <h3 style={{ fontFamily: theme.fonts.serif, fontSize: '1.5rem', marginBottom: '0.5rem' }}>2. Agendamento</h3>
              <p style={{ color: '#666', fontSize: '0.9rem' }}>Definimos o melhor dia e horário para sua rotina.</p>
            </StepCard>

            <StepCard>
              <div className="step-icon"><FaRoute /></div>
              <h3 style={{ fontFamily: theme.fonts.serif, fontSize: '1.5rem', marginBottom: '0.5rem' }}>3. Início</h3>
              <p style={{ color: '#666', fontSize: '0.9rem' }}>Começamos sua jornada de autoconhecimento e bem-estar.</p>
            </StepCard>
          </StepsGrid>
        </Container>
      </section>

      <section id="faq" style={{ padding: '6rem 0', position: 'relative', background: 'rgba(255,255,255,0.4)' }}>
        <Container>
          <div style={{ textAlign: 'center', marginBottom: '4rem' }} className="reveal">
            <span style={{ fontSize: '0.75rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: theme.colors.sageDark, fontWeight: 'bold' }}>Esclareça seus pontos</span>
            <h2 style={{ fontFamily: theme.fonts.serif, fontSize: 'clamp(2rem, 4vw, 3rem)', color: theme.colors.earthDark }}>Dúvidas Frequentes</h2>
          </div>

          <FAQGrid>
            <FAQCard className="reveal">
              <div style={{ marginTop: '5px' }}><FaCoins size={24} color={theme.colors.sageDark} /></div>
              <div>
                <h3 style={{ fontFamily: theme.fonts.serif, fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>Qual o valor das sessões?</h3>
                <p style={{ fontSize: '0.875rem', color: '#4b5563', lineHeight: '1.6' }}>O código de ética não permite a divulgação pública de valores. Mas entre em contato pelo botão do WhatsApp que te passo todas as informações sem compromisso!</p>
              </div>
            </FAQCard>

            <FAQCard className="reveal">
              <div style={{ marginTop: '5px' }}><FaUserShield size={24} color={theme.colors.sageDark} /></div>
              <div>
                <h3 style={{ fontFamily: theme.fonts.serif, fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>É sigiloso?</h3>
                <p style={{ fontSize: '0.875rem', color: '#4b5563', lineHeight: '1.6' }}>Absolutamente. Seguimos rigorosamente o código de ética. O ambiente online é criptografado e seguro para você falar livremente.</p>
              </div>
            </FAQCard>

            <FAQCard className="reveal">
              <div style={{ marginTop: '5px' }}><FaFileMedicalAlt size={24} color={theme.colors.sageDark} /></div>
              <div>
                <h3 style={{ fontFamily: theme.fonts.serif, fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>O resultado é um diagnóstico?</h3>
                <p style={{ fontSize: '0.875rem', color: '#4b5563', lineHeight: '1.6' }}>Sim, quando necessário. O laudo pode confirmar ou descartar hipóteses diagnósticas e orientar o tratamento.</p>
              </div>
            </FAQCard>

            <FAQCard className="reveal">
              <div style={{ marginTop: '5px' }}><FaBalanceScale size={24} color={theme.colors.sageDark} /></div>
              <div>
                <h3 style={{ fontFamily: theme.fonts.serif, fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>Tem respaldo jurídico?</h3>
                <p style={{ fontSize: '0.875rem', color: '#4b5563', lineHeight: '1.6' }}>Sim. Os laudos seguem critérios técnicos reconhecidos pelo CFP, permitindo uso em contextos jurídicos, médicos e escolares.</p>
              </div>
            </FAQCard>

            <FAQCard className="reveal" style={{ gridColumn: '1 / -1' }}>
              <div style={{ marginTop: '5px' }}><FaPuzzlePiece size={24} color={theme.colors.sageDark} /></div>
              <div style={{ width: '100%' }}>
                <h3 style={{ fontFamily: theme.fonts.serif, fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>Qual a diferença entre análise psicológica e neuropsicológica?</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '2rem' }}>
                  <div>
                    <strong style={{ display: 'block', marginBottom: '0.5rem', color: theme.colors.sageDark }}>Análise Psicológica</strong>
                    <p style={{ fontSize: '0.875rem', color: '#4b5563' }}>Foca em aspectos emocionais, de personalidade e comportamentais.</p>
                  </div>
                  <div>
                    <strong style={{ display: 'block', marginBottom: '0.5rem', color: theme.colors.sageDark }}>Análise Neuropsicológica</strong>
                    <p style={{ fontSize: '0.875rem', color: '#4b5563' }}>Investiga como as funções cognitivas (memória, atenção) impactam a vida prática.</p>
                  </div>
                </div>
              </div>
            </FAQCard>
          </FAQGrid>
        </Container>
      </section>

      <section id="contato" style={{ padding: '8rem 0', background: `linear-gradient(135deg, ${theme.colors.sageDark} 0%, #7d9182 100%)`, color: 'white', textAlign: 'center' }}>
        <Container className="reveal">
          <h2 style={{ fontFamily: theme.fonts.serif, fontSize: 'clamp(2rem, 5vw, 3rem)', marginBottom: '1.5rem' }}>Sua mente é seu lar.</h2>
          <p style={{ fontSize: '1.1rem', opacity: 0.9, marginBottom: '3rem', maxWidth: '40rem', margin: '0 auto 3rem', lineHeight: '1.6' }}>
            Vamos cuidar dele com o carinho que merece? Agende sua sessão online e comece a priorizar você.
          </p>
          <Button href={getWaUrl("Olá Luana, decidi agendar minha sessão agora.")} target="_blank" style={{ backgroundColor: 'white', color: theme.colors.sageDark, padding: '1.2rem 3.5rem', fontSize: '0.9rem' }}>
            Agendar Atendimento Agora
          </Button>
        </Container>
      </section>

      <footer style={{ backgroundColor: theme.colors.sand, padding: '4rem 0 2rem', borderTop: '1px solid rgba(0,0,0,0.05)' }}>
        <Container>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.5rem', marginBottom: '4rem' }}>
            <Logo href="#"><span><FaSpa /></span> Luana Mayara</Logo>
            <div style={{ textAlign: 'center' }}>
              <p style={{ fontSize: '1rem', fontWeight: '500', color: theme.colors.sageDark, margin: 0 }}>Psicóloga Clínica</p>
              <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.2rem' }}>CRP 06/202392 | Neuropsicologia & TCC</p>
            </div>
            <div style={{ display: 'flex', gap: '1.8rem' }}>
              <a href="https://instagram.com/luanamayara.psico" target="_blank" rel="noopener noreferrer" style={{ color: theme.colors.sageDark, fontSize: '1.5rem', transition: '0.3s' }}><FaInstagram /></a>
              <a href={getWaUrl("Olá, vim pelo rodapé.")} style={{ color: theme.colors.sageDark, fontSize: '1.5rem', transition: '0.3s' }}><FaWhatsapp /></a>
              <a href="mailto:contato@luanamayara.com.br" style={{ color: theme.colors.sageDark, fontSize: '1.5rem', transition: '0.3s' }}><FaEnvelope /></a>
            </div>
          </div>
          <div style={{ width: '100%', height: '1px', backgroundColor: 'rgba(0,0,0,0.05)', marginBottom: '2rem' }}></div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', fontSize: '0.7rem', color: '#9CA3AF', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '1.5rem', textAlign: 'center' }}>
              <span>&copy; 2025 Luana Mayara Psicologia.</span>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <a href="#" style={{ color: '#9CA3AF', textDecoration: 'none' }}>Privacidade</a>
                <a href="#" style={{ color: '#9CA3AF', textDecoration: 'none' }}>Termos</a>
              </div>
            </div>
            <div style={{ marginTop: '0.5rem', padding: '0.5rem 1rem', borderRadius: '20px', backgroundColor: 'rgba(0,0,0,0.02)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span>Powered by</span>
              <a href="https://hellfy.com/" target="_blank" rel="noopener noreferrer" style={{ color: '#6B7280', fontWeight: 'bold', textDecoration: 'none' }}>HELLFY</a>
              <span style={{ opacity: 0.3 }}>|</span>
              <a href="https://www.app-loquaz.com/" target="_blank" rel="noopener noreferrer" style={{ color: '#9CA3AF', textDecoration: 'none' }}>LOQUAZ</a>
            </div>
          </div>
        </Container>
      </footer>

      <WhatsAppFloat href={getWaUrl("Olá, preciso de ajuda rápida.")} target="_blank">
        <FaWhatsapp />
      </WhatsAppFloat>
    </ThemeProvider>
  );
}

export default App;